import { observable, action, toJS, makeObservable } from 'mobx';
import { BaseViewStore } from 'core/stores';
import { InventorySyncForm } from 'modules/product-inventory/forms';
import { ApplicationStore } from 'platform/application/stores';
import { isSome, isValueNullOrNegative, notificationError, isWarehouse } from 'core/utils';
import { ProductInventoryStore } from 'modules/product-inventory/stores';
import { saveAs } from 'file-saver';
import { fileTypes, sourceOfChange } from 'common/constants';
import { ModalParams } from 'core/models';
import excelJS from 'exceljs';
import XLSX from 'xlsx';
import _ from 'lodash';

class InventorySyncPageViewStore extends BaseViewStore {
	@observable realWarehouseOptions = [];
	@observable csvWarehouseOptions = [];
	@observable fileSelected = false;
	@observable productInventory = [];
	@observable fileName;
	@observable lastModifyFileDate;
	@observable updateTable = true;
	@observable confirmDisabled = true;
	originalData = [];
	updateActivity = [];
	editField = 'inEdit';
	noDataItem = true;
	allowEdit = true;
	cancelDisabled = true;
	skuQuantity = [];

	form = new InventorySyncForm({
		onSuccess: async () => {
			await this.inventorySync();
		},
	});

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.applicationStore = new ApplicationStore(rootStore);
		this.productInventoryStore = new ProductInventoryStore(rootStore);
		this.tableData = {
			columns: [
				{
					key: 'sku',
					title: 'PRODUCTDETAILVIEW.INVENTORY.SKU',
					selected: true,
					width: 200,
					filter: null,
					editable: false,
				},
				{
					key: 'productName',
					title: 'PRODUCTDETAILVIEW.INVENTORY.PRODUCTNAME',
					selected: true,
					width: 200,
					filter: null,
					editable: false,
				},
				{
					key: 'warehouseQuantity',
					title: 'PRODUCTDETAILVIEW.INVENTORY.WHQTY',
					selected: true,
					width: 200,
					filter: null,
					editable: false,
				},
				{
					key: 'quantity',
					title: 'PRODUCTDETAILVIEW.INVENTORY.QTY',
					selected: true,
					width: 200,
					filter: null,
					editable: true,
					editor: 'numeric',
				},
				{
					key: 'difference',
					title: 'PRODUCTDETAILVIEW.INVENTORY.DIFFERENCE',
					selected: true,
					width: 200,
					filter: null,
					editable: false,
				},
				{
					key: 'updatedWarehouseOnHoldQuantity',
					title: 'PRODUCTDETAILVIEW.INVENTORY.UPDATEDWAREHOUSEONHOLDQUANTITY',
					selected: true,
					width: 200,
					filter: null,
					editable: false,
				},
				{
					key: 'warehouseOnHoldQuantity',
					title: 'PRODUCTDETAILVIEW.INVENTORY.WAREHOUSEONHOLDQUANTITY',
					selected: true,
					width: 200,
					filter: null,
					editable: true,
					editor: 'numeric',
				},
				{
					key: 'warehouseOnHoldQuantityDifference',
					title: 'PRODUCTDETAILVIEW.INVENTORY.DIFFERENCE',
					selected: true,
					width: 200,
					filter: null,
					editable: false,
				},
				{
					key: 'note',
					title: 'PRODUCTDETAILVIEW.INVENTORY.NOTE',
					selected: true,
					width: 200,
					filter: null,
					editable: true,
					editor: 'text',
				},
			],
			sortObject: [],
			actions: {},
		};
		this.isWarehouse = isWarehouse(rootStore);
		this.initialize();
	}

	@action
	initialize = async () => {
		this.form.state.options.set({
			validateOnBlur: false,
			showErrorsOnReset: false,
			validateOnChange: true,
		});
		this.form.reset();
		await this.getWarehouseOptions();
	};

	@action
	inventorySync = async () => {
		const { warehouse } = this.form.values();
		await this.productInventoryStore.sync({
			warehouseId: warehouse.value,
			inventory: this.skuQuantity.filter(x => x.warehouseId == warehouse.value),
		});
		if (this.productInventoryStore.response.length === 0) {
			this.rootStore.notificationStore.error(['MAINMENU.INVENTORY.NOPRODUCTINVENTORYMATCHFOUND']);
		} else {
			this.productInventory = this.productInventoryStore.response.item;
			_.forEach(this.productInventory, (inventory, index) => {
				inventory.inEdit = false;
				inventory.whqty = true;
				inventory.index = index;
			});
			this.cancelDisabled = true;
			this.initialProductInventories = _.cloneDeep(this.productInventory.slice());
		}
	};

	@action.bound
	onItemChange = event => {
		const itemIndex = _.findIndex(this.productInventory, item => {
			return item == event.dataItem;
		});
		this.productInventory[itemIndex][event.field] = event.value;
		this.rerenderTable();
	};

	@action.bound async validateInputs() {
		var isXlsValid = (await this.form.$('excel').validate()).isValid || this.fileSelected;
		var isWarehouseValid = (await this.form.$('warehouse').validate()).isValid;
		return isXlsValid && isWarehouseValid;
	}

	@action.bound async clearData() {
		this.csvWarehouseOptions = [];
		this.skuQuantity = [];
		this.productInventory = [];
	}

	@action.bound async clearSyncData() {
		this.productInventory = [];
	}

	@action.bound
	downloadTemplate = () => {
		const workbook = new excelJS.Workbook();
		workbook.created = new Date();
		workbook.modified = new Date();
		const sheet = workbook.addWorksheet('ImportWarehouseInventoryTemplate');
		sheet.columns = [
			{ header: 'Warehouse', key: 'Warehouse' },
			{ header: 'SKU', key: 'SKU' },
			{ header: 'Quantity', key: 'Quantity' },
			{ header: 'WhOnHoldQuantity', key: 'WhOnHoldQuantity' },
		];
		workbook.xlsx.writeBuffer().then(function(buffer) {
			const blob = new Blob([buffer], { type: 'applicationi/xlsx' });
			saveAs(blob, 'ImportWarehouseInventoryTemplate.xlsx');
		});
	};

	@action.bound async onFilesDrop(filesMeta) {
		this.clearData();
		this.fileSelected = false;
		if (filesMeta && filesMeta.length == 1) {
			let fileMeta = filesMeta[0];
			this.fileName = fileMeta.name;
			this.lastModifyFileDate = fileMeta.lastModifiedDate;
			let reader = new FileReader();
			if (
				fileMeta.type === fileTypes.xlsx ||
				(fileMeta.type === fileTypes.xls && !fileMeta.name.includes('csv', fileMeta.name.length - 3))
			) {
				reader.onload = async evt => {
					const workbook = XLSX.read(evt.target.result, { type: 'binary' });
					const firstWorksheet = workbook.Sheets[workbook.SheetNames[0]];
					let data = XLSX.utils.sheet_to_json(firstWorksheet);
					if (data.length > 0) {
						this.processExcelFile(data);
						if (this.form.$('warehouse').value != '') this.form.$('warehouse').value = null;
						this.fileSelected = true;
						this.loading = false;
					} else {
						this.loading = false;
						this.rootStore.notificationStore.error(['PRODUCTVIEW.NOVALIDDATAFORIMPORTFOUND']);
					}
				};
				reader.readAsBinaryString(fileMeta);
			} else {
				this.loading = false;
				this.rootStore.notificationStore.error(['PRODUCTVIEW.PRODUCTIMPORTERBADFORMAT']);
			}
		} else {
			this.rootStore.notificationStore.error(['PRODUCTVIEW.PRODUCTIMPORTERROR']);
		}
	}

	processExcelFile(data) {
		// Excel processing (excluding csv)
		_.forEach(data, product => {
			if (this.validateColumns(product)) {
				const wh = this.realWarehouseOptions.find(
					x => x.applicationTitle.toLowerCase() == product.Warehouse.toLowerCase()
				);
				if (wh != undefined) {
					//Check if sku inside same warehouse has duplicate values
					if (
						this.skuQuantity.findIndex(
							x => x.sku == product.SKU && wh.applicationTitle.toLowerCase() == x.warehouse.toLowerCase()
						) != -1
					) {
						this.setValidationError([
							'PRODUCTDETAILVIEW.INVENTORY.NOTIFICATION.DUPLICATEVALUES',
							{ sku: product.SKU, warehouse: product.Warehouse },
						]);
						return false;
					}

					//Prepare list of warehouses for dropdown
					if (this.csvWarehouseOptions.findIndex(x => x.label.toLowerCase() == product.Warehouse.toLowerCase()) == -1) {
						this.csvWarehouseOptions.push({
							value: wh.id,
							label: product.Warehouse,
						});
					}

					//If all checks passed, add product to reconciliation list
					this.composeSkuQuantity(product, wh);
				} else {
					this.rootStore.notificationStore.warning('PRODUCTDETAILVIEW.INVENTORY.NOTIFICATION.WAREHOUSEDOESNOTEXIST');
				}
			} else {
				this.setValidationError(['PRODUCTDETAILVIEW.INVENTORY.NOTIFICATION.RECONCILIATIONVALIDATIONERROR']);
				return false;
			}
		});
	}

	setValidationError(errors) {
		this.loading = false;
		this.csvWarehouseOptions = [];
		this.rootStore.notificationStore.error(errors);
	}
	composeSkuQuantity(product, wh) {
		this.skuQuantity.push({
			sku: product.SKU,
			quantity: product.Quantity,
			warehouseOnHoldQuantity: product.WhOnHoldQuantity,
			warehouse: product.Warehouse,
			warehouseId: wh.id,
		});
	}

	validateColumns(item) {
		if (
			item.hasOwnProperty('SKU') &&
			item.hasOwnProperty('Quantity') &&
			item.hasOwnProperty('WhOnHoldQuantity') &&
			item.hasOwnProperty('Warehouse') &&
			this.validateNumberField(item.Quantity) &&
			this.validateNumberField(item.WhOnHoldQuantity)
		) {
			return true;
		} else {
			return false;
		}
	}

	validateNumberField = item => {
		if (!isNaN(item) && item % 1 == 0 && item >= 0) {
			return true;
		} else {
			return false;
		}
	};

	@action.bound
	async getWarehouseOptions() {
		await this.applicationStore.find({ rpp: 150, page: 1, warehouseOnly: true, isOrganizationActive: true });
		this.realWarehouseOptions = this.applicationStore.data;
	}

	@action.bound
	onSelectChange = async (selectedOption, field) => {
		field.value = selectedOption;
		this.form.$('note').clear();
		await this.inventorySync();
	};

	rerenderTable() {
		this.updateTable = !this.updateTable;
	}

	@action.bound
	edit = dataItem => {
		dataItem.inEdit = true;
		if (dataItem.id) {
			const index = _.findIndex(this.productInventory, item => {
				return item.id == dataItem.id;
			});
			this.productInventory[index] = dataItem;
		}
		this.rerenderTable();
	};

	// Cancel button handler for individual product edit.
	@action.bound
	cancel = dataItem => {
		if (isSome(dataItem.id)) {
			var initialProductInventory = this.initialProductInventories.find(item => {
				return item.id == dataItem.id;
			});
			const index = _.findIndex(this.productInventory, item => {
				return item.id == dataItem.id;
			});
			this.productInventory[index] = initialProductInventory;
		} else {
			dataItem.inEdit = false;
		}
		if (this.updateActivity.length > 0) {
			_.remove(
				this.updateActivity,
				item => item.productId === dataItem.productId && item.warehouseId === dataItem.warehouseId
			);
		}
		this.checkActivities();
		this.rerenderTable();
	};

	@action.bound
	onCancelBatch = () => {
		this.form.$('note').clear();
		this.productInventory = [...this.initialProductInventories.slice()];
		this.updateActivity = [];
		this.cancelDisabled = true;
		this.checkActivities();
		this.rerenderTable();
	};

	@action.bound
	adjustAllSkus = async () => {
		this.updateActivity = [];
		_.forEach(this.productInventory, inventory => {
			inventory.quantity = inventory.warehouseQuantity;
			inventory.warehouseOnHoldQuantity = inventory.updatedWarehouseOnHoldQuantity;
			inventory.note = this.form.$('note').value;
			this.calculateQuantityDifferences(inventory);
			this.update(inventory);
		});
		_.forEach(this.updateActivity, activity => {
			activity.description = this.form.$('note').value;
		});
		this.adjustAllSkusModal.close();
		this.rerenderTable();
	};

	//Update single row in a inventory grid
	@action.bound
	update = productInventory => {
		if (
			isValueNullOrNegative(productInventory.quantity) ||
			isValueNullOrNegative(productInventory.warehouseOnHoldQuantity) ||
			productInventory.quantity.toString().includes('.')
		) {
			this.rootStore.notificationStore.error(
				'PRODUCTDETAILVIEW.INVENTORY.NOTIFICATION.RECONCILIATIONVALIDATIONERRORNOHOLD'
			);
		} else {
			const initialProductInventory = this.initialProductInventories.find(item => {
				return item.id === productInventory.id;
			});
			if (isSome(initialProductInventory)) {
				this.handleWarehouseActivityPerProduct(productInventory, initialProductInventory);
			} else {
				this.rootStore.notificationStore.error('PRODUCTDETAILVIEW.INVENTORY.NOTIFICATION.ERROR');
			}
			this.calculateQuantityDifferences(productInventory);
			productInventory.inEdit = false;
			this.checkActivities();
			this.rerenderTable();
		}
	};

	handleWarehouseActivityPerProduct(productInventory, initialProductInventory) {
		const existingItemIdx = _.findIndex(this.updateActivity, item => {
			return item.index === productInventory.index;
		});
		if (
			productInventory.quantity != initialProductInventory.quantity ||
			productInventory.warehouseOnHoldQuantity != initialProductInventory.warehouseOnHoldQuantity
		) {
			//if change on product quantites occured update whActivity for that product, if activity exists
			const warehouseActivity = this.createWarehouseActivity(productInventory, initialProductInventory);
			if (existingItemIdx > -1) {
				this.updateActivity[existingItemIdx] = warehouseActivity;
			} else {
				this.updateActivity.push(warehouseActivity);
			}
		} else {
			//if change didn't occure, remove activity for that product if exists
			if (existingItemIdx > -1) {
				this.updateActivity.splice(existingItemIdx, 1);
			}
		}
	}

	calculateQuantityDifferences(productInventory) {
		productInventory.difference = productInventory.warehouseQuantity - productInventory.quantity;
		productInventory.warehouseOnHoldQuantityDifference =
			productInventory.updatedWarehouseOnHoldQuantity - productInventory.warehouseOnHoldQuantity;
	}

	@action.bound
	checkActivities = () => {
		this.confirmDisabled =
			this.updateActivity.length === 0 ||
			_.find(this.productInventory, item => {
				return item.inEdit === true || item.warehouseId === null;
			}) != null;
		this.cancelDisabled = this.updateActivity.length === 0;
	};

	createWarehouseActivity = (productInventory, initialProductInventory) => {
		return {
			index: productInventory.index,
			isReceived: productInventory.id ? false : true,
			warehouseId: productInventory.warehouseId,
			productId: productInventory.productId,
			quantity: productInventory.quantity - initialProductInventory.quantity,
			description: productInventory.note,
			isAdjustment: productInventory.isNewInventory ? false : true,
			isInventoryAdded: productInventory.isNewInventory ? true : false,
			quantityBefore: initialProductInventory.quantity,
			quantityAfter: productInventory.quantity,
			warehouseOnHoldQuantityBefore: initialProductInventory.warehouseOnHoldQuantity,
			warehouseOnHoldQuantityAfter: productInventory.warehouseOnHoldQuantity,
			warehouseOnHoldQuantity:
				productInventory.warehouseOnHoldQuantity - initialProductInventory.warehouseOnHoldQuantity,
		};
	};

	@action.bound updateInventory = async () => {
		this.suspend();
		try {
			var dataToBeSent = this.composeDataForInventoryUpdate();
			await this.productInventoryStore.createBatch(
				toJS({
					productInventories: dataToBeSent,
					warehouseActivities: this.updateActivity,
					isWarehouse: this.isWarehouse,
				}),
				sourceOfChange.inventorySync
			);
			_.each(this.productInventory, item => {
				var existingItem = _.find(
					this.productInventoryStore.item.data,
					savedItem => savedItem.productId === item.productId && savedItem.assignedWarehouseId === item.warehouseId
				);
				item.id = existingItem.id;
			});
			this.rootStore.notificationStore.success('PRODUCTDETAILVIEW.INVENTORY.NOTIFICATION.SUCCESS');
			this.updateActivity = [];
			this.checkActivities();
			this.clearSyncData();
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.initialize();
		this.resume();
	};

	composeDataForInventoryUpdate() {
		return _.map(this.productInventory.slice(), item => {
			return {
				...item,
				assignedWarehouseId: item.warehouseId,
			};
		});
	}
	adjustAllSkusModal = new ModalParams();
}

export default InventorySyncPageViewStore;



// WEBPACK FOOTER //
// ./src/modules/product-inventory/stores/InventorySyncPageViewStore.js